(function () {
  let renderTimer;
  let isRendering = false;
  let renderQueued = false;
  const depositVariantId = 55334564692302;
  const globalFreeShippingPromo = true;
  const productCache = new Map();

  function formatEUR(cents) {
    return (cents / 100).toLocaleString('de-DE', {
      style: 'currency',
      currency: 'EUR'
    });
  }

  function getShippingRelevantTotal(cart) {
    return (cart.items || []).reduce((sum, item) => {
      if (item.variant_id === depositVariantId) return sum;
      return sum + (item.final_line_price || 0);
    }, 0);
  }

  function getItemHandle(item) {
    if (item.handle) return item.handle;
    if (!item.url) return '';
    const parts = item.url.split('/products/');
    return parts[1] ? parts[1].split('?')[0].split('/')[0] : '';
  }

  async function getProductData(item) {
    const handle = getItemHandle(item);
    if (!handle) return null;

    if (!productCache.has(handle)) {
      productCache.set(
        handle,
        fetch(`/products/${handle}.js`, { credentials: 'same-origin' })
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      );
    }

    return productCache.get(handle);
  }

  async function getCompareAtLinePrice(item) {
    const product = await getProductData(item);
    const variant = product?.variants?.find((candidate) => candidate.id === item.variant_id);
    const compareAtPrice = variant?.compare_at_price || 0;
    const price = variant?.price || item.original_price || 0;

    if (compareAtPrice <= price) return 0;
    return compareAtPrice * (item.quantity || 1);
  }

  async function hasFreeShippingProduct(cart) {
    for (const item of cart.items || []) {
      if (item.variant_id === depositVariantId) continue;

      const product = await getProductData(item);
      const tags = Array.isArray(product?.tags) ? product.tags : [];
      if (tags.some((tag) => `${tag}`.toLowerCase() === 'versandkostenfrei')) return true;
    }

    return false;
  }

  async function getSavings(cart) {
    let referenceTotal = 0;
    let paidTotal = 0;

    for (const item of cart.items || []) {
      if (item.variant_id === depositVariantId) continue;

      const compareAtLinePrice = await getCompareAtLinePrice(item);
      const originalLinePrice = item.original_line_price || 0;
      const finalLinePrice = item.final_line_price || 0;
      const referenceLinePrice = Math.max(compareAtLinePrice, originalLinePrice, finalLinePrice);

      referenceTotal += referenceLinePrice;
      paidTotal += finalLinePrice;
    }

    const amount = Math.max(0, referenceTotal - paidTotal);
    const percent = referenceTotal > 0 ? Math.round((amount * 100) / referenceTotal) : 0;

    return { amount, percent };
  }

  function setHintState(hint, html, progress) {
    const progressValue = Math.max(0, Math.min(100, progress));
    const stateKey = `${html}|${progressValue}`;
    if (hint.dataset.lastFreeShippingHint === stateKey) return;

    hint.dataset.lastFreeShippingHint = stateKey;

    let bar = hint.querySelector('.free-shipping-hint__bar span');
    let text = hint.querySelector('.free-shipping-hint__text');

    if (!bar || !text) {
      hint.innerHTML = [
        '<div class="free-shipping-hint__bar" aria-hidden="true"><span></span></div>',
        '<p class="free-shipping-hint__text"></p>'
      ].join('');
      bar = hint.querySelector('.free-shipping-hint__bar span');
      text = hint.querySelector('.free-shipping-hint__text');
    }

    bar.style.width = `${progressValue}%`;
    text.innerHTML = html;
  }

  function setSavingsState(hint, savings) {
    let savingsHint = hint.querySelector('.cart-savings-hint');

    if (!savings.amount || savings.percent <= 0) {
      if (savingsHint) savingsHint.remove();
      return;
    }

    if (!savingsHint) {
      savingsHint = document.createElement('p');
      savingsHint.className = 'cart-savings-hint';
      hint.appendChild(savingsHint);
    }

    savingsHint.textContent = `Du sparst aktuell ${savings.percent} %.`;
  }

  function scheduleRenderFreeShippingHint(delay) {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(renderFreeShippingHint, delay || 0);
  }

  async function renderFreeShippingHint() {
    if (isRendering) {
      renderQueued = true;
      return;
    }

    const hint = document.querySelector('#CartDrawer #free-shipping-hint');
    if (!hint) return;

    isRendering = true;

    try {
      const res = await fetch('/cart.js', { credentials: 'same-origin' });
      const cart = await res.json();
      const threshold = parseInt(hint.dataset.threshold || '3500', 10);
      const savings = await getSavings(cart);

      if (globalFreeShippingPromo) {
        setHintState(hint, 'Deine Bestellung ist versandkostenfrei.', 100);
        setSavingsState(hint, savings);
        return;
      }

      const hasTaggedFreeShippingProduct = await hasFreeShippingProduct(cart);

      const hasFreeShippingDiscount = [
        ...(cart.cart_level_discount_applications || []),
        ...(cart.items || []).flatMap((item) => item.discounts || []),
      ].some((discount) => {
        const title = (discount.title || '').toLowerCase();
        return title.includes('versandkostenfrei') || title.includes('kostenloser versand');
      });

      if (hint.dataset.freeShipping === 'true' || hasTaggedFreeShippingProduct || hasFreeShippingDiscount) {
        setHintState(hint, 'Deine Bestellung ist versandkostenfrei.', 100);
        setSavingsState(hint, savings);
        return;
      }

      const shippingRelevantTotal = getShippingRelevantTotal(cart);
      const remaining = threshold - shippingRelevantTotal;
      const progress = threshold > 0 ? (shippingRelevantTotal * 100) / threshold : 100;

      setHintState(
        hint,
        remaining > 0
          ? `Nur noch <strong>${formatEUR(remaining)}</strong> bis zum kostenlosen Versand`
          : 'Deine Bestellung ist versandkostenfrei.',
        remaining > 0 ? progress : 100
      );
      setSavingsState(hint, savings);
    } catch (e) {
      console.warn('Free shipping hint error', e);
    } finally {
      isRendering = false;
      if (renderQueued) {
        renderQueued = false;
        scheduleRenderFreeShippingHint(150);
      }
    }
  }

  document.addEventListener('DOMContentLoaded', () => scheduleRenderFreeShippingHint());

  function shouldRenderForMutation(mutations) {
    return mutations.some((mutation) => {
      if (mutation.target?.closest?.('#free-shipping-hint')) return false;

      return [...mutation.addedNodes, ...mutation.removedNodes].some((node) => {
        if (node.nodeType !== Node.ELEMENT_NODE) return false;
        return (
          node.matches?.('#free-shipping-hint, cart-drawer-items, .cart-drawer__footer') ||
          node.querySelector?.('#free-shipping-hint, cart-drawer-items, .cart-drawer__footer')
        );
      });
    });
  }

  const drawer = document.getElementById('CartDrawer');
  if (drawer) {
    const obs = new MutationObserver((mutations) => {
      if (shouldRenderForMutation(mutations) && document.querySelector('#CartDrawer #free-shipping-hint')) {
        scheduleRenderFreeShippingHint(100);
      }
    });
    obs.observe(drawer, { childList: true, subtree: true });
  }

  document.addEventListener('cart:updated', () => scheduleRenderFreeShippingHint());
  document.addEventListener('cart:refresh', () => scheduleRenderFreeShippingHint());

  document.addEventListener('click', function (e) {
    if (
      e.target.closest('cart-remove-button') ||
      e.target.closest('quantity-input') ||
      e.target.closest('[name="plus"]') ||
      e.target.closest('[name="minus"]') ||
      e.target.closest('button[name="add"]')
    ) {
      scheduleRenderFreeShippingHint(250);
    }
  });
})();
